import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Search, GraduationCap, Menu, X, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { db } from './Config/Config';
import logo from './assets/westberglogo2png.png';
import { PrivacyPolicy, TermsAndConditions, RefundPolicy } from './pages/Compliance';

// Import Global Components
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import RegisterModal from './components/RegisterModal';

const Home = React.lazy(() => import('./pages/Home'));
const AboutUs = React.lazy(() => import('./pages/AboutUs'));
const Campuses = React.lazy(() => import('./pages/Campuses'));
const ContactUs = React.lazy(() => import('./pages/ContactUs'));
const Admissions = React.lazy(() => import('./pages/Admissions'));
const DynamicPage = React.lazy(() => import('./pages/DynamicPage'));
const CoursesHub = React.lazy(() => import('./pages/CoursesHub'));
const CourseDetail = React.lazy(() => import('./pages/CourseDetail'));
const CareersHub = React.lazy(() => import('./pages/CareersHub'));
const JobDetail = React.lazy(() => import('./pages/JobDetail'));
const Placements = React.lazy(() => import('./pages/Placements'));
const Gallery = React.lazy(() => import('./pages/Gallery'));
const BlogList = React.lazy(() => import('./pages/blogs/BlogList'));
const BlogDetail = React.lazy(() => import('./pages/blogs/BlogDetail'));
const RoadMap = React.lazy(() => import('./pages/Roadmap/RoadMap'));

// Top Navbar Component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Navbar = React.memo(({ dynamicLinks, blogs }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

  const toggleDropdown = (name) => {
    if (openDropdown === name) setOpenDropdown(null);
    else setOpenDropdown(name);
  };

  return (
    <nav className="fixed top-0 left-0 w-full z-50 bg-cream border-b border-rich-black/5">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-4 z-50" onClick={() => setIsMenuOpen(false)}>
          <img src={logo} alt="Westberg Logo" className="h-14 w-auto" />
          {/* <span className="text-2xl font-serif font-bold text-rich-black tracking-tight pr-5">WESTBERG</span> */}
        </Link>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-8 h-full">
          <Link to="/" className={`text-[10px] font-bold uppercase tracking-widest transition-colors h-full flex items-center border-b-2 ${location.pathname === '/' ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>Home</Link>

          <Link to="/about" className={`text-[10px] font-bold uppercase tracking-widest transition-colors h-full flex items-center border-b-2 ${location.pathname === '/about' ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>About</Link>
          <Link to="/roadmap" className={`text-[10px] font-bold uppercase tracking-widest transition-colors h-full flex items-center border-b-2 ${location.pathname === '/roadmap' ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>Roadmap</Link>
          <Link to="/campuses" className={`text-[10px] font-bold uppercase tracking-widest transition-colors h-full flex items-center border-b-2 ${location.pathname === '/campuses' ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>Campuses</Link>

          {/* Courses Dropdown */}
          <div className="relative h-full flex items-center group" onMouseLeave={() => setOpenDropdown(null)} onMouseEnter={() => setOpenDropdown('courses')}>
            <button className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest transition-colors h-full border-b-2 ${location.pathname.includes('/courses') ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>
              Courses <ChevronDown size={14} className="mt-0.5 text-[#8C363D]" />
            </button>
            <AnimatePresence>
              {openDropdown === 'courses' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 w-56 bg-white border border-rich-black/5 shadow-2xl py-4 flex flex-col z-50"
                >
                  <Link to="/courses" className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16] hover:bg-cream border-l-2 border-transparent hover:border-[#4A0F16] transition-all">All Courses</Link>
                  <Link to="/courses/diploma" className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16] hover:bg-cream border-l-2 border-transparent hover:border-[#4A0F16] transition-all">Diploma</Link>
                  <Link to="/courses/advanced-diploma" className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16] hover:bg-cream border-l-2 border-transparent hover:border-[#4A0F16] transition-all">Advanced Diploma</Link>
                  <Link to="/courses/pg-diploma" className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16] hover:bg-cream border-l-2 border-transparent hover:border-[#4A0F16] transition-all">PG Diploma</Link>
                  <Link to="/courses/certification-course" className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16] hover:bg-cream border-l-2 border-transparent hover:border-[#4A0F16] transition-all">Certification Course</Link>
                  <Link to="/courses/degree" className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16] hover:bg-cream border-l-2 border-transparent hover:border-[#4A0F16] transition-all">Degree</Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/admissions" className={`text-[10px] font-bold uppercase tracking-widest transition-colors h-full flex items-center border-b-2 ${location.pathname === '/admissions' ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>Admissions</Link>
          <Link to="/placements" className={`text-[10px] font-bold uppercase tracking-widest transition-colors h-full flex items-center border-b-2 ${location.pathname === '/placements' ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>Placements</Link>
          <Link to="/gallery" className={`text-[10px] font-bold uppercase tracking-widest transition-colors h-full flex items-center border-b-2 ${location.pathname === '/gallery' ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>Gallery</Link>

          {/* Blogs Dropdown */}
          <div className="relative h-full flex items-center group" onMouseLeave={() => setOpenDropdown(null)} onMouseEnter={() => setOpenDropdown('blogs')}>
            <Link to="/blog" className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest transition-colors h-full border-b-2 ${location.pathname.includes('/blog') ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>
              Blog <ChevronDown size={14} className="mt-0.5 text-[#8C363D]" />
            </Link>
            <AnimatePresence>
              {openDropdown === 'blogs' && blogs.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 w-72 bg-white border border-rich-black/5 shadow-2xl py-4 flex flex-col z-50"
                >
                  {blogs.slice(0, 5).map(blog => (
                    <Link key={blog.id} to={`/blog/${blog.slug}`} className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16] hover:bg-cream border-l-2 border-transparent hover:border-[#4A0F16] transition-all truncate">
                      {blog.title}
                    </Link>
                  ))}
                  <Link to="/blog" className="px-6 py-4 text-[10px] font-bold text-[#4A0F16] hover:bg-[#4A0F16] hover:text-white mt-2 border-t border-rich-black/5 uppercase tracking-widest text-center transition-colors">View All Posts</Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/careers" className={`text-[10px] font-bold uppercase tracking-widest transition-colors h-full flex items-center border-b-2 ${location.pathname === '/careers' ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>Careers</Link>
          <Link to="/contact" className={`text-[10px] font-bold uppercase tracking-widest transition-colors h-full flex items-center border-b-2 ${location.pathname === '/contact' ? 'text-[#4A0F16] border-[#4A0F16]' : 'text-slate-500 hover:text-[#4A0F16] border-transparent hover:border-[#4A0F16]'}`}>Contact Us</Link>

          {/* CTA Button */}
          <div className="flex items-center h-full ml-4 gap-4">
            <button
              onClick={() => setIsRegisterModalOpen(true)}
              className="px-6 py-3 bg-[#4A0F16] text-white text-[10px] font-bold uppercase tracking-widest hover:bg-[#8C363D] transition-colors border border-rich-black/10 shadow-sm flex items-center justify-center h-[38px]"
            >
              Register
            </button>
          </div>

        </div>

        {/* Mobile Toggle */}
        <div className="flex items-center gap-4 lg:gap-6 z-50">
          <button
            className="lg:hidden text-rich-black hover:text-[#4A0F16] transition-colors p-2 -mr-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -20, height: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="lg:hidden absolute top-24 left-0 w-full bg-white border-b border-rich-black/5 shadow-2xl flex flex-col px-6 py-8 space-y-2 font-medium max-h-[80vh] overflow-y-auto origin-top z-40"
          >
            <Link to="/" onClick={() => setIsMenuOpen(false)} className={`py-4 text-[10px] font-bold uppercase tracking-widest border-b border-rich-black/5 ${location.pathname === '/' ? 'text-[#4A0F16]' : 'text-slate-500'}`}>Home</Link>
            <Link to="/about" onClick={() => setIsMenuOpen(false)} className={`py-4 text-[10px] font-bold uppercase tracking-widest border-b border-rich-black/5 ${location.pathname === '/about' ? 'text-[#4A0F16]' : 'text-slate-500'}`}>About Us</Link>
            <Link to="/roadmap" onClick={() => setIsMenuOpen(false)} className={`py-4 text-[10px] font-bold uppercase tracking-widest border-b border-rich-black/5 ${location.pathname === '/roadmap' ? 'text-[#4A0F16]' : 'text-slate-500'}`}>Roadmap</Link>
            <Link to="/campuses" onClick={() => setIsMenuOpen(false)} className={`py-4 text-[10px] font-bold uppercase tracking-widest border-b border-rich-black/5 ${location.pathname === '/campuses' ? 'text-[#4A0F16]' : 'text-slate-500'}`}>Campuses</Link>

            {/* Courses Mobile Dropdown */}
            <div className="border-b border-rich-black/5">
              <button
                onClick={() => toggleDropdown('mobile-courses')}
                className={`w-full flex items-center justify-between py-4 text-[10px] font-bold uppercase tracking-widest ${location.pathname.includes('/courses') ? 'text-[#4A0F16]' : 'text-slate-500'}`}
              >
                Courses <ChevronDown size={14} className={`transition-transform ${openDropdown === 'mobile-courses' ? 'rotate-180 text-[#8C363D]' : ''}`} />
              </button>
              <AnimatePresence>
                {openDropdown === 'mobile-courses' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex flex-col pl-4 pb-2 space-y-1 border-l-2 border-[#4A0F16]/20 overflow-hidden bg-cream"
                  >
                    <Link to="/courses" onClick={() => setIsMenuOpen(false)} className="py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16]">All Courses</Link>
                    <Link to="/courses/diploma" onClick={() => setIsMenuOpen(false)} className="py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16]">Diploma</Link>
                    <Link to="/courses/advanced-diploma" onClick={() => setIsMenuOpen(false)} className="py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16]">Advanced Diploma</Link>
                    <Link to="/courses/pg-diploma" onClick={() => setIsMenuOpen(false)} className="py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16]">PG Diploma</Link>
                    <Link to="/courses/certification-course" onClick={() => setIsMenuOpen(false)} className="py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16]">Certification Course</Link>
                    <Link to="/courses/degree" onClick={() => setIsMenuOpen(false)} className="py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16]">Degree</Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/admissions" onClick={() => setIsMenuOpen(false)} className={`py-4 text-[10px] font-bold uppercase tracking-widest border-b border-rich-black/5 ${location.pathname === '/admissions' ? 'text-[#4A0F16]' : 'text-slate-500'}`}>Admissions</Link>
            <Link to="/placements" onClick={() => setIsMenuOpen(false)} className={`py-4 text-[10px] font-bold uppercase tracking-widest border-b border-rich-black/5 ${location.pathname === '/placements' ? 'text-[#4A0F16]' : 'text-slate-500'}`}>Placements</Link>
            <Link to="/gallery" onClick={() => setIsMenuOpen(false)} className={`py-4 text-[10px] font-bold uppercase tracking-widest border-b border-rich-black/5 ${location.pathname === '/gallery' ? 'text-[#4A0F16]' : 'text-slate-500'}`}>Gallery</Link>

            {/* Blog Mobile Dropdown */}
            <div className="border-b border-rich-black/5">
              <button
                onClick={() => toggleDropdown('mobile-blogs')}
                className={`w-full flex items-center justify-between py-4 text-[10px] font-bold uppercase tracking-widest ${location.pathname.includes('/blog') ? 'text-[#4A0F16]' : 'text-slate-500'}`}
              >
                Blog <ChevronDown size={14} className={`transition-transform ${openDropdown === 'mobile-blogs' ? 'rotate-180 text-[#8C363D]' : ''}`} />
              </button>
              <AnimatePresence>
                {openDropdown === 'mobile-blogs' && blogs.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex flex-col pl-4 pb-2 space-y-1 border-l-2 border-[#4A0F16]/20 overflow-hidden bg-cream"
                  >
                    {blogs.slice(0, 5).map(blog => (
                      <Link key={blog.id} to={`/blog/${blog.slug}`} onClick={() => setIsMenuOpen(false)} className="py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#4A0F16] truncate">
                        {blog.title}
                      </Link>
                    ))}
                    <Link to="/blog" onClick={() => setIsMenuOpen(false)} className="py-3 mt-2 text-[10px] font-bold uppercase tracking-widest text-[#4A0F16] hover:text-[#8C363D]">View All Posts</Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link to="/careers" onClick={() => setIsMenuOpen(false)} className={`py-4 text-[10px] font-bold uppercase tracking-widest border-b border-rich-black/5 ${location.pathname === '/careers' ? 'text-[#4A0F16]' : 'text-slate-500'}`}>Careers</Link>
            <Link to="/contact" onClick={() => setIsMenuOpen(false)} className={`py-4 text-[10px] font-bold uppercase tracking-widest border-b border-rich-black/5 ${location.pathname === '/contact' ? 'text-[#4A0F16]' : 'text-slate-500'}`}>Contact Us</Link>

            <button
              onClick={() => { setIsRegisterModalOpen(true); setIsMenuOpen(false); }}
              className="py-4 mt-4 bg-[#4A0F16] text-white text-center text-[10px] font-bold uppercase tracking-widest hover:bg-[#8C363D] transition-colors border border-rich-black/10 shadow-sm"
            >
              Register
            </button>

          </motion.div>
        )}
      </AnimatePresence>
      <RegisterModal isOpen={isRegisterModalOpen} onClose={() => setIsRegisterModalOpen(false)} />
    </nav>
  );
});

const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 30, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -30, scale: 0.98 }}
    transition={{ duration: 0.6, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-[#4A0F16] rounded-full animate-spin mb-4"></div>
          <p className="text-[#4A0F16] font-bold tracking-widest text-xs uppercase animate-pulse">Loading...</p>
        </div>
      }>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Home /></PageTransition>} />
          <Route path="/about" element={<PageTransition><AboutUs /></PageTransition>} />
          <Route path="/campuses" element={<PageTransition><Campuses /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><ContactUs /></PageTransition>} />
          <Route path="/admissions" element={<PageTransition><Admissions /></PageTransition>} />
          <Route path="/placements" element={<PageTransition><Placements /></PageTransition>} />
          <Route path="/gallery" element={<PageTransition><Gallery /></PageTransition>} />

          <Route path="/courses" element={<PageTransition><CoursesHub /></PageTransition>} />
          <Route path="/courses/:category" element={<PageTransition><CoursesHub /></PageTransition>} />
          <Route path="/courses/:category/:slug" element={<PageTransition><CourseDetail /></PageTransition>} />

          <Route path="/careers" element={<PageTransition><CareersHub /></PageTransition>} />
          <Route path="/careers/:id" element={<PageTransition><JobDetail /></PageTransition>} />

          <Route path="/blog" element={<PageTransition><BlogList /></PageTransition>} />
          <Route path="/blog/:slug" element={<PageTransition><BlogDetail /></PageTransition>} />

          <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
          <Route path="/terms" element={<PageTransition><TermsAndConditions /></PageTransition>} />
          <Route path="/refund-policy" element={<PageTransition><RefundPolicy /></PageTransition>} />
          <Route path="/roadmap" element={<PageTransition><RoadMap /></PageTransition>} />

          <Route path="/:slug" element={<PageTransition><DynamicPage /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

function App() {
  const [links, setLinks] = useState([]);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch CMS Pages
        const qPages = query(collection(db, 'pages'), where('status', '==', true));
        const snapshotPages = await getDocs(qPages);
        setLinks(snapshotPages.docs.map(doc => doc.data()));

        // Fetch Blogs for Navbar Submenu
        const qBlogs = query(collection(db, 'blogs'), where('status', '==', true));
        const snapshotBlogs = await getDocs(qBlogs);
        setBlogs(snapshotBlogs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="min-h-[100dvh] bg-slate-50 font-sans text-slate-800 flex flex-col overflow-x-hidden">
          <Navbar dynamicLinks={links} blogs={blogs} />

          <main className="flex-1 pt-24">
            <AnimatedRoutes />
          </main>

          <Footer />
          <WhatsAppButton />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
